import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TranscriptionsTab.css';
import Toast from './Toast';
import ConfirmationModal from './ConfirmationModal';

const API_URL = process.env.REACT_APP_API_URL || '';
const PAGE_SIZE = 12;

function TranscriptionsTab() {
  const [transcriptions, setTranscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTranscription, setSelectedTranscription] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [editedSummary, setEditedSummary] = useState('');
  const [questionsList, setQuestionsList] = useState([]); // Список вопросов с ответами
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null); // Индекс редактируемого вопроса
  const [editingQuestion, setEditingQuestion] = useState({ question: '', answer: '' }); // Данные редактируемого вопроса
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState(null);
  const [showMockInterviewModal, setShowMockInterviewModal] = useState(false);
  const [generatingQuestion, setGeneratingQuestion] = useState(false);
  const [regeneratingAnswerIndex, setRegeneratingAnswerIndex] = useState(null);
  /** ID расшифровки, для которой идёт генерация вопросов по тексту (кнопка «Создать вопросы по расшифровке») */
  const [generatingQuestionsFromId, setGeneratingQuestionsFromId] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [mockInterviewForm, setMockInterviewForm] = useState({
    company_id: '',
    position: '',
    grade: 'Junior',
    salary_range: '',
    questions: []
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' — с конца (новые), 'asc' — с начала (старые)
  const [searchQuery, setSearchQuery] = useState(''); // значение в поле ввода
  const [appliedSearch, setAppliedSearch] = useState(''); // поисковой запрос, отправляемый на API

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    fetchTranscriptions();
  }, [currentPage, sortOrder, appliedSearch]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/companies`);
      setCompanies(response.data);
    } catch (err) {
      console.error('Ошибка при загрузке компаний:', err);
    }
  };

  const fetchTranscriptions = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * PAGE_SIZE;
      const params = { skip, limit: PAGE_SIZE, order: sortOrder };
      if (appliedSearch.trim()) {
        params.search = appliedSearch.trim();
      }
      const countParams = appliedSearch.trim() ? { search: appliedSearch.trim() } : {};
      const [listRes, countRes] = await Promise.all([
        axios.get(`${API_URL}/api/transcriptions`, { params }),
        axios.get(`${API_URL}/api/transcriptions/count`, { params: countParams })
      ]);
      setTranscriptions(listRes.data);
      setTotalCount(countRes.data.count);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке расшифровок: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatTime = (timeMs) => {
    if (timeMs === undefined || timeMs === null) return '';
    const totalSeconds = Math.floor(timeMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    } else {
      return `${minutes}:${String(seconds).padStart(2, '0')}`;
    }
  };

  const handleViewDetails = (transcription) => {
    setSelectedTranscription(transcription);
    setEditedText(transcription.transcription_text);
    setEditedSummary(transcription.summary || '');
    setIsEditing(false);
    setIsEditingSummary(false);
    
    // Парсим вопросы из суммаризации
    const parsedQuestions = parseSummaryQuestions(transcription.summary);
    if (parsedQuestions) {
      setQuestionsList(parsedQuestions);
    } else {
      setQuestionsList([]);
    }
    
    setEditingQuestionIndex(null);
    setEditingQuestion({ question: '', answer: '' });
  };

  const handleCloseDetails = () => {
    setSelectedTranscription(null);
    setIsEditing(false);
    setIsEditingSummary(false);
    setEditedText('');
    setEditedSummary('');
    setQuestionsList([]);
    setEditingQuestionIndex(null);
    setEditingQuestion({ question: '', answer: '' });
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditedText(selectedTranscription.transcription_text);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedText(selectedTranscription.transcription_text);
  };

  const handleSaveEdit = async () => {
    if (!selectedTranscription || !editedText.trim()) {
      showToast('Текст не может быть пустым', 'warning');
      return;
    }

    try {
      setSaving(true);
      const updateData = {
        transcription_text: editedText.trim()
      };
      
      // Добавляем summary, если он редактировался
      if (isEditingSummary) {
        updateData.summary = editedSummary.trim() || null;
      }
      
      const response = await axios.put(
        `${API_URL}/api/transcriptions/${selectedTranscription.filename}`,
        updateData
      );

      // Обновляем локальное состояние
      const updatedTranscription = response.data;
      setSelectedTranscription(updatedTranscription);
      setEditedText(updatedTranscription.transcription_text);
      setEditedSummary(updatedTranscription.summary || '');
      setTranscriptions(transcriptions.map(t => 
        t.id === updatedTranscription.id ? updatedTranscription : t
      ));
      setIsEditing(false);
      setIsEditingSummary(false);
      setError(null);
      showToast('Изменения сохранены успешно!', 'success');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Неизвестная ошибка';
      setError('Ошибка при сохранении: ' + errorMessage);
      showToast('Ошибка при сохранении изменений: ' + errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSummary = () => {
    setIsEditingSummary(true);
    setEditedSummary(selectedTranscription.summary || '');
  };

  const handleCancelEditSummary = () => {
    setIsEditingSummary(false);
    setEditedSummary(selectedTranscription.summary || '');
    // Восстанавливаем список вопросов из исходной суммаризации
    const parsedQuestions = parseSummaryQuestions(selectedTranscription.summary);
    if (parsedQuestions) {
      setQuestionsList(parsedQuestions);
    } else {
      setQuestionsList([]);
    }
    setEditingQuestionIndex(null);
    setEditingQuestion({ question: '', answer: '' });
  };

  const handleSaveSummary = async () => {
    if (!selectedTranscription) {
      return;
    }

    try {
      setSaving(true);
      
      // Если есть список вопросов, преобразуем его в JSON
      let summaryToSave = editedSummary.trim() || null;
      if (questionsList.length > 0) {
        summaryToSave = questionsToJSONString(questionsList);
      }
      
      const response = await axios.put(
        `${API_URL}/api/transcriptions/${selectedTranscription.filename}`,
        {
          summary: summaryToSave
        }
      );

      const updatedTranscription = response.data;
      setSelectedTranscription(updatedTranscription);
      setEditedSummary(updatedTranscription.summary || '');
      
      // Обновляем список вопросов
      const parsedQuestions = parseSummaryQuestions(updatedTranscription.summary);
      if (parsedQuestions) {
        setQuestionsList(parsedQuestions);
      } else {
        setQuestionsList([]);
      }
      
      setTranscriptions(transcriptions.map(t => 
        t.id === updatedTranscription.id ? updatedTranscription : t
      ));
      setIsEditingSummary(false);
      setEditingQuestionIndex(null);
      setEditingQuestion({ question: '', answer: '' });
      setError(null);
      showToast('Суммаризация сохранена успешно!', 'success');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Неизвестная ошибка';
      setError('Ошибка при сохранении суммаризации: ' + errorMessage);
      showToast('Ошибка при сохранении суммаризации: ' + errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Функции для работы с вопросами
  const handleEditQuestion = (index) => {
    setEditingQuestionIndex(index);
    setEditingQuestion({
      question: questionsList[index].question || '',
      answer: questionsList[index].answer || ''
    });
  };

  const handleCancelEditQuestion = () => {
    setEditingQuestionIndex(null);
    setEditingQuestion({ question: '', answer: '' });
  };

  const handleSaveQuestion = (index) => {
    if (!editingQuestion.question.trim() && !editingQuestion.answer.trim()) {
      showToast('Вопрос и ответ не могут быть пустыми одновременно', 'warning');
      return;
    }

    const updatedQuestions = [...questionsList];
    updatedQuestions[index] = {
      question: editingQuestion.question.trim(),
      answer: editingQuestion.answer.trim()
    };
    setQuestionsList(updatedQuestions);
    
    // Автоматически обновляем editedSummary для сохранения
    setEditedSummary(questionsToJSONString(updatedQuestions));
    
    setEditingQuestionIndex(null);
    setEditingQuestion({ question: '', answer: '' });
    showToast('Вопрос обновлен!', 'success');
  };

  const handleDeleteQuestion = (index) => {
    const updatedQuestions = questionsList.filter((_, i) => i !== index);
    setQuestionsList(updatedQuestions);
    
    // Автоматически обновляем editedSummary для сохранения
    if (updatedQuestions.length > 0) {
      setEditedSummary(questionsToJSONString(updatedQuestions));
    } else {
      setEditedSummary('');
    }
    
    if (editingQuestionIndex === index) {
      setEditingQuestionIndex(null);
      setEditingQuestion({ question: '', answer: '' });
    } else if (editingQuestionIndex > index) {
      // Корректируем индекс редактируемого вопроса, если он был после удаленного
      setEditingQuestionIndex(editingQuestionIndex - 1);
    }
    
    showToast('Вопрос удален!', 'success');
  };

  const handleAddQuestion = () => {
    const newQuestion = { question: '', answer: '' };
    const updatedQuestions = [...questionsList, newQuestion];
    setQuestionsList(updatedQuestions);
    
    // Автоматически обновляем editedSummary для сохранения
    setEditedSummary(questionsToJSONString(updatedQuestions));
    
    // Начинаем редактирование нового вопроса
    setEditingQuestionIndex(updatedQuestions.length - 1);
    setEditingQuestion(newQuestion);
  };

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  const hideToast = () => {
    setToast(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast('Текст скопирован в буфер обмена!', 'success');
  };

  // Функция для парсинга JSON из суммаризации (поддержка различных структур с question и answer)
  const parseSummaryQuestions = (summaryText) => {
    if (!summaryText || !summaryText.trim()) {
      return null;
    }

    try {
      // Удаляем markdown обрамление (``` в начале и конце)
      let cleanedText = summaryText.trim();
      if (cleanedText.startsWith('```')) {
        const lines = cleanedText.split('\n');
        // Удаляем первую строку с ```
        if (lines[0].trim().startsWith('```')) {
          lines.shift();
        }
        // Удаляем последнюю строку с ```
        if (lines.length > 0 && lines[lines.length - 1].trim() === '```') {
          lines.pop();
        }
        cleanedText = lines.join('\n').trim();
      }
      
      // Удаляем оставшиеся обратные кавычки
      cleanedText = cleanedText.replace(/^`+|`+$/g, '').trim();
      
      // Парсим JSON
      const parsed = JSON.parse(cleanedText);
      
      let questions = [];
      
      // Формат 1: Массив объектов [{text: {question, answer}}, ...]
      if (Array.isArray(parsed)) {
        questions = parsed
          .map(item => {
            if (typeof item === 'object' && item !== null) {
              // Структура: item.text содержит объект с question и answer
              if (item.text && typeof item.text === 'object' && !Array.isArray(item.text)) {
                return {
                  question: item.text.question || '',
                  answer: item.text.answer || ''
                };
              }
              // Старая структура для обратной совместимости
              if (item.question && item.answer) {
                return {
                  question: item.question,
                  answer: item.answer
                };
              }
              // Если только text как строка
              if (typeof item.text === 'string') {
                return {
                  question: item.text,
                  answer: ''
                };
              }
            }
            return null;
          })
          .filter(q => q !== null && (q.question || q.answer));
      }
      // Формат 2: Объект с полем text, которое является массивом {text: [{question, answer}, ...]}
      else if (typeof parsed === 'object' && parsed !== null && Array.isArray(parsed.text)) {
        questions = parsed.text
          .map(item => {
            if (typeof item === 'object' && item !== null) {
              return {
                question: item.question || '',
                answer: item.answer || ''
              };
            }
            return null;
          })
          .filter(q => q !== null && (q.question || q.answer));
      }
      // Формат 3: Объект с полями text1, text2, ... {text1: {question, answer}, text2: {question, answer}, ...}
      else if (typeof parsed === 'object' && parsed !== null) {
        // Ищем все поля, которые начинаются с "text" и содержат объект с question и answer
        const textKeys = Object.keys(parsed).filter(key => 
          key.startsWith('text') && 
          typeof parsed[key] === 'object' && 
          parsed[key] !== null &&
          !Array.isArray(parsed[key])
        );
        
        if (textKeys.length > 0) {
          // Сортируем ключи по числовому значению (text1, text2, text10 -> text1, text2, text10)
          textKeys.sort((a, b) => {
            const numA = parseInt(a.replace('text', '')) || 0;
            const numB = parseInt(b.replace('text', '')) || 0;
            return numA - numB;
          });
          
          questions = textKeys
            .map(key => {
              const item = parsed[key];
              return {
                question: item.question || '',
                answer: item.answer || ''
              };
            })
            .filter(q => q !== null && (q.question || q.answer));
        }
        // Также проверяем, есть ли поле "text" как объект (не массив)
        else if (parsed.text && typeof parsed.text === 'object' && !Array.isArray(parsed.text)) {
          questions = [{
            question: parsed.text.question || '',
            answer: parsed.text.answer || ''
          }].filter(q => q !== null && (q.question || q.answer));
        }
      }
      
      return questions.length > 0 ? questions : null;
    } catch (error) {
      // Если не удалось распарсить как JSON, возвращаем null
      // Это означает, что суммаризация в другом формате
      console.warn('Failed to parse summary JSON:', error);
      return null;
    }
  };

  // Функция для преобразования списка вопросов обратно в JSON строку
  const questionsToJSONString = (questions) => {
    if (!questions || questions.length === 0) {
      return '';
    }
    
    const jsonArray = questions.map(q => ({
      text: {
        question: q.question || '',
        answer: q.answer || ''
      }
    }));
    
    return JSON.stringify(jsonArray, null, 2);
  };

  const handleDelete = async (transcription, event) => {
    // Предотвращаем всплытие события, если кнопка находится внутри карточки
    if (event) {
      event.stopPropagation();
    }

    // Показываем модальное окно подтверждения
    setConfirmationModal({
      isOpen: true,
      title: 'Подтверждение удаления',
      message: `Вы уверены, что хотите удалить расшифровку для файла "${transcription.filename}"?\n\nЭто действие нельзя отменить.`,
      onConfirm: async () => {
        setConfirmationModal(null);
        try {
          await axios.delete(`${API_URL}/api/transcriptions/${transcription.filename}`);
          
          // Обновляем список и счётчик
          fetchTranscriptions();
          
          // Если удаляемая расшифровка была открыта в модальном окне, закрываем его
          if (selectedTranscription && selectedTranscription.id === transcription.id) {
            handleCloseDetails();
          }
          
          setError(null);
          showToast('Расшифровка успешно удалена!', 'success');
        } catch (err) {
          const errorMessage = err.response?.data?.detail || err.message || 'Неизвестная ошибка';
          setError('Ошибка при удалении расшифровки: ' + errorMessage);
          showToast('Ошибка при удалении расшифровки: ' + errorMessage, 'error');
        }
      },
      onCancel: () => {
        setConfirmationModal(null);
      },
      confirmText: 'Удалить',
      cancelText: 'Отмена',
      confirmButtonType: 'danger'
    });
  };

  const handleCreateMockInterview = (transcription) => {
    // Парсим вопросы из суммаризации
    const parsedQuestions = parseSummaryQuestions(transcription.summary);
    const initialQuestions = parsedQuestions || [];
    
    setMockInterviewForm({
      company_id: '',
      position: '',
      grade: 'Junior',
      salary_range: '',
      questions: initialQuestions
    });
    setShowMockInterviewModal(true);
  };

  /** Генерация списка вопросов по полному тексту расшифровки и открытие формы Mock-собеседования с этими вопросами (ответы пустые). */
  const handleCreateQuestionsFromTranscription = async (transcription) => {
    const text = (transcription.transcription_text || '').trim();
    if (!text) {
      showToast('Нет текста расшифровки для генерации вопросов', 'warning');
      return;
    }
    setGeneratingQuestionsFromId(transcription.id);
    setError(null);
    try {
      const response = await axios.post(`${API_URL}/api/questions/generate`, { text });
      const list = response.data?.questions;
      const rawList = Array.isArray(list) ? list.map((q) => (typeof q === 'string' ? q : String(q || '')).trim()) : [];
      if (rawList.length > 0) {
        if (rawList[0].startsWith('[')) rawList[0] = rawList[0].slice(1).trim();
        if (rawList[rawList.length - 1].endsWith(']')) rawList[rawList.length - 1] = rawList[rawList.length - 1].slice(0, -1).trim();
      }
      const questions = rawList.map((q) => ({ question: q, answer: '' }));
      setMockInterviewForm({
        company_id: '',
        position: '',
        grade: 'Junior',
        salary_range: '',
        questions: questions.length > 0 ? questions : [{ question: '', answer: '' }]
      });
      setShowMockInterviewModal(true);
      showToast(questions.length > 0 ? `Создано ${questions.length} вопросов` : 'Вопросы не найдены. Добавьте вопрос вручную.', questions.length > 0 ? 'success' : 'info');
    } catch (err) {
      showToast('Ошибка при генерации вопросов: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setGeneratingQuestionsFromId(null);
    }
  };

  const handleCloseMockInterviewModal = () => {
    setShowMockInterviewModal(false);
    setGeneratingQuestion(false);
    setMockInterviewForm({
      company_id: '',
      position: '',
      grade: 'Junior',
      salary_range: '',
      questions: []
    });
  };

  const handleAddMockQuestion = () => {
    setMockInterviewForm({
      ...mockInterviewForm,
      questions: [...mockInterviewForm.questions, { question: '', answer: '' }]
    });
  };

  const handleUpdateMockQuestion = (index, field, value) => {
    const updatedQuestions = [...mockInterviewForm.questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      [field]: value
    };
    setMockInterviewForm({
      ...mockInterviewForm,
      questions: updatedQuestions
    });
  };

  const handleDeleteMockQuestion = (index) => {
    const updatedQuestions = mockInterviewForm.questions.filter((_, i) => i !== index);
    setMockInterviewForm({
      ...mockInterviewForm,
      questions: updatedQuestions
    });
  };

  const handleRegenerateMockAnswer = async (index) => {
    const questionText = (mockInterviewForm.questions[index]?.question || '').trim();
    if (!questionText) {
      showToast('Введите текст вопроса для пересоздания ответа', 'warning');
      return;
    }
    try {
      setRegeneratingAnswerIndex(index);
      const response = await axios.post(`${API_URL}/api/questions/regenerate-answer`, {
        question_text: questionText,
      });
      const answer = response.data?.correct_answer ?? '';
      handleUpdateMockQuestion(index, 'answer', answer);
      showToast('Ответ пересоздан', 'success');
    } catch (err) {
      showToast('Ошибка при пересоздании ответа: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setRegeneratingAnswerIndex(null);
    }
  };

  const handleGenerateMockQuestion = async () => {
    const questions = mockInterviewForm.questions.filter(q => (q.question || '').trim() && (q.answer || '').trim());
    if (questions.length === 0) {
      showToast('Добавьте хотя бы один вопрос с ответом для генерации нового', 'warning');
      return;
    }
    try {
      setGeneratingQuestion(true);
      const response = await axios.post(`${API_URL}/api/transcriptions/generate-additional-question`, {
        questions: questions.map(q => ({ question: q.question.trim(), answer: q.answer.trim() }))
      });
      const { question } = response.data;
      setMockInterviewForm({
        ...mockInterviewForm,
        questions: [{ question: question || '', answer: '' }, ...mockInterviewForm.questions]
      });
      showToast('Вопрос успешно сгенерирован', 'success');
    } catch (err) {
      showToast('Ошибка при генерации вопроса: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setGeneratingQuestion(false);
    }
  };

  const handleSubmitMockInterview = async () => {
    // Валидация
    if (!mockInterviewForm.company_id) {
      showToast('Выберите компанию', 'warning');
      return;
    }
    if (!mockInterviewForm.position.trim()) {
      showToast('Введите название должности', 'warning');
      return;
    }
    if (!mockInterviewForm.salary_range.trim()) {
      showToast('Введите зарплатную вилку', 'warning');
      return;
    }
    if (mockInterviewForm.questions.length === 0) {
      showToast('Добавьте хотя бы один вопрос', 'warning');
      return;
    }

    // Проверяем, что все вопросы заполнены
    for (let i = 0; i < mockInterviewForm.questions.length; i++) {
      const q = mockInterviewForm.questions[i];
      if (!q.question.trim() || !q.answer.trim()) {
        showToast(`Заполните вопрос и ответ для вопроса ${i + 1}`, 'warning');
        return;
      }
    }

    try {
      setSaving(true);
      
      // Формируем данные для отправки
      const interviewData = {
        company_id: parseInt(mockInterviewForm.company_id),
        title: `${mockInterviewForm.position} - ${mockInterviewForm.grade}`,
        position: mockInterviewForm.position,
        grade: mockInterviewForm.grade,
        salary_range: mockInterviewForm.salary_range,
        questions: mockInterviewForm.questions.map(q => ({
          question: q.question.trim(),
          answer: q.answer.trim()
        }))
      };

      await axios.post(`${API_URL}/api/mock-interviews`, interviewData);
      
      showToast('Mock-собеседование успешно создано!', 'success');
      handleCloseMockInterviewModal();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Неизвестная ошибка';
      showToast('Ошибка при создании mock-собеседования: ' + errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setAppliedSearch(searchQuery);
    setCurrentPage(1);
  };

  const handleSortChange = (order) => {
    setSortOrder(order);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1;

  if (loading && transcriptions.length === 0) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="transcriptions-tab">
      <div className="tab-header">
        <h2>Результаты распознавания</h2>
        <button className="btn btn-secondary" onClick={fetchTranscriptions} disabled={loading}>
          🔄 Обновить
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="info-box">
        <p>📝 Здесь отображаются завершенные распознавания речи с их результатами.</p>
      </div>

      <div className="transcriptions-filters">
        <form className="transcriptions-search-form" onSubmit={handleSearchSubmit}>
          <input
            type="text"
            className="transcriptions-search-input"
            placeholder="Поиск по названию распознавания..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit" className="btn btn-primary btn-search">
            Искать
          </button>
          {appliedSearch && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setSearchQuery('');
                setAppliedSearch('');
                setCurrentPage(1);
              }}
            >
              Сбросить
            </button>
          )}
        </form>
        <div className="transcriptions-sort">
          <span className="sort-label">Порядок:</span>
          <label className="sort-option">
            <input
              type="radio"
              name="sortOrder"
              checked={sortOrder === 'desc'}
              onChange={() => handleSortChange('desc')}
            />
            С конца (новые)
          </label>
          <label className="sort-option">
            <input
              type="radio"
              name="sortOrder"
              checked={sortOrder === 'asc'}
              onChange={() => handleSortChange('asc')}
            />
            С начала (старые)
          </label>
        </div>
        <div className="transcriptions-results-info">
          Найдено: <strong>{totalCount}</strong>
        </div>
      </div>

      {transcriptions.length === 0 && !loading ? (
        <div className="empty-state-container">
          <div className="empty-state-icon">📭</div>
          <p className="empty-state-text">
            {appliedSearch
              ? 'По запросу ничего не найдено.'
              : 'Нет результатов распознавания.'}
          </p>
          <p className="empty-state-hint">
            {appliedSearch
              ? 'Попробуйте изменить поисковый запрос или сбросить фильтр.'
              : 'Результаты появятся здесь после завершения обработки аудиофайлов.'}
          </p>
        </div>
      ) : (
        <>
          <div className="transcriptions-grid">
            {transcriptions.map((transcription) => (
              <div key={transcription.id} className="transcription-card">
                <div className="card-header">
                  <div className="card-title-section">
                    <h3 className="card-filename">{transcription.filename}</h3>
                    <span className="card-date">{formatDate(transcription.created_at)}</span>
                  </div>
                  <div className="card-actions">
                    <button
                      className="btn-icon btn-view"
                      onClick={() => handleViewDetails(transcription)}
                      title="Просмотреть детали"
                    >
                      👁️
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={(e) => handleDelete(transcription, e)}
                      title="Удалить расшифровку"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                <div className="card-content">
                  <div className="card-metadata">
                    {transcription.language_code && (
                      <span className="metadata-badge language-badge">
                        🌐 {transcription.language_code}
                      </span>
                    )}
                    {transcription.transcription_data?.speaker_count && 
                     transcription.transcription_data.speaker_count > 0 && (
                      <span className="metadata-badge speakers-badge">
                        👥 {transcription.transcription_data.speaker_count} спикер(ов)
                      </span>
                    )}
                    {transcription.summary && (
                      <span className="metadata-badge summary-badge">
                        📊 Есть суммаризация
                      </span>
                    )}
                  </div>
                  <div className="transcription-preview">
                    {transcription.transcription_text.length > 200
                      ? transcription.transcription_text.substring(0, 200) + '...'
                      : transcription.transcription_text}
                  </div>
                  <div className="card-footer">
                    {transcription.s3_url && (
                      <a
                        href={transcription.s3_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="s3-link"
                      >
                        🔗 Открыть файл в S3
                      </a>
                    )}
                    <button
                      type="button"
                      className="btn btn-small btn-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCreateQuestionsFromTranscription(transcription);
                      }}
                      disabled={generatingQuestionsFromId === transcription.id || !(transcription.transcription_text || '').trim()}
                      title={
                        !(transcription.transcription_text || '').trim()
                          ? 'Нет текста расшифровки'
                          : 'Сгенерировать вопросы по тексту расшифровки и открыть форму Mock-собеседования'
                      }
                    >
                      {generatingQuestionsFromId === transcription.id ? '⏳ Генерация...' : '📝 Создать вопросы по расшифровке'}
                    </button>
                    {transcription.summary && (
                      <button
                        className="btn btn-small btn-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateMockInterview(transcription);
                        }}
                        title="Создать Mock-собеседование"
                      >
                        🎯 Создать Mock-собеседование
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="transcriptions-pagination">
              <div className="pagination-info">
                Страница {currentPage} из {totalPages}
              </div>
              <div className="pagination-controls">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1 || loading}
                >
                  ← Назад
                </button>
                <div className="pagination-pages">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (currentPage <= 3) pageNum = i + 1;
                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = currentPage - 2 + i;
                    return (
                      <button
                        key={pageNum}
                        type="button"
                        className={`btn pagination-page ${currentPage === pageNum ? 'active' : ''}`}
                        onClick={() => setCurrentPage(pageNum)}
                        disabled={loading}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages || loading}
                >
                  Вперед →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {selectedTranscription && (
        <div className="modal-overlay" onClick={handleCloseDetails}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Детали расшифровки</h2>
              <button className="btn-icon btn-close" onClick={handleCloseDetails}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              {/* Суммаризация - отображается первой */}
              <div className="detail-section">
                <div className="detail-header">
                  <label>📊 Суммаризация:</label>
                  <div className="detail-header-buttons">
                    {!isEditingSummary ? (
                      <>
                        <button
                          className="btn btn-small btn-edit"
                          onClick={handleEditSummary}
                        >
                          ✏️ Редактировать
                        </button>
                        {selectedTranscription.summary && questionsList.length > 0 && (
                          <button
                            className="btn btn-small"
                            onClick={() => {
                              // Копируем форматированный список вопросов с ответами
                              const formattedText = questionsList
                                .map((q, idx) => `${idx + 1}. ${q.question}\n   Ответ: ${q.answer}`)
                                .join('\n\n');
                              copyToClipboard(formattedText);
                            }}
                          >
                            📋 Копировать
                          </button>
                        )}
                        {!isEditingSummary && questionsList.length > 0 && (
                          <button
                            className="btn btn-small btn-primary"
                            onClick={handleAddQuestion}
                          >
                            ➕ Добавить вопрос
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <button
                          className="btn btn-small btn-save"
                          onClick={handleSaveSummary}
                          disabled={saving}
                        >
                          {saving ? '💾 Сохранение...' : '💾 Сохранить'}
                        </button>
                        <button
                          className="btn btn-small btn-cancel"
                          onClick={handleCancelEditSummary}
                          disabled={saving}
                        >
                          ❌ Отмена
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {isEditingSummary ? (
                  <div className="summary-edit-mode">
                    {questionsList.length > 0 ? (
                      <div className="questions-list-container">
                        {questionsList.map((item, index) => (
                          <div key={index} className="question-item-container">
                            <div className="question-item-header">
                              <span className="question-number">{index + 1}.</span>
                              <div className="question-item-actions">
                                {editingQuestionIndex === index ? (
                                  <>
                                    <button
                                      className="btn btn-tiny btn-save"
                                      onClick={() => handleSaveQuestion(index)}
                                      disabled={saving}
                                    >
                                      ✓ Сохранить
                                    </button>
                                    <button
                                      className="btn btn-tiny btn-cancel"
                                      onClick={handleCancelEditQuestion}
                                      disabled={saving}
                                    >
                                      ✕ Отмена
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      className="btn btn-tiny btn-edit"
                                      onClick={() => handleEditQuestion(index)}
                                      disabled={saving}
                                    >
                                      ✏️ Редактировать
                                    </button>
                                    <button
                                      className="btn btn-tiny btn-delete"
                                      onClick={() => handleDeleteQuestion(index)}
                                      disabled={saving}
                                    >
                                      🗑️ Удалить
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                            {editingQuestionIndex === index ? (
                              <div className="question-edit-form">
                                <div className="question-edit-field">
                                  <label>Вопрос:</label>
                                  <textarea
                                    className="question-edit-textarea"
                                    value={editingQuestion.question}
                                    onChange={(e) => setEditingQuestion({ ...editingQuestion, question: e.target.value })}
                                    rows={2}
                                    disabled={saving}
                                    placeholder="Введите вопрос..."
                                  />
                                </div>
                                <div className="question-edit-field">
                                  <label>Ответ:</label>
                                  <textarea
                                    className="question-edit-textarea"
                                    value={editingQuestion.answer}
                                    onChange={(e) => setEditingQuestion({ ...editingQuestion, answer: e.target.value })}
                                    rows={3}
                                    disabled={saving}
                                    placeholder="Введите ответ..."
                                  />
                                </div>
                              </div>
                            ) : (
                              <div className="question-display">
                                <div className="question-text">
                                  <strong>Вопрос:</strong> {item.question || '(вопрос не указан)'}
                                </div>
                                <div className="answer-text">
                                  <strong>Ответ:</strong> {item.answer || '(ответ не указан)'}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                        <button
                          className="btn btn-primary btn-add-question"
                          onClick={handleAddQuestion}
                          disabled={saving}
                        >
                          ➕ Добавить новый вопрос
                        </button>
                      </div>
                    ) : (
                      <div className="summary-empty-state">
                        <p>Вопросы отсутствуют. Добавьте первый вопрос:</p>
                        <button
                          className="btn btn-primary"
                          onClick={handleAddQuestion}
                          disabled={saving}
                        >
                          ➕ Добавить вопрос
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="summary-content">
                    {questionsList.length > 0 ? (
                      <ol className="summary-questions-list">
                        {questionsList.map((item, index) => (
                          <li key={index} className="summary-question-item">
                            <div className="question-display-view">
                              <div className="question-text-view">
                                <strong>Вопрос:</strong> {item.question || '(вопрос не указан)'}
                              </div>
                              <div className="answer-text-view">
                                <strong>Ответ:</strong> {item.answer || '(ответ не указан)'}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ol>
                    ) : selectedTranscription.summary ? (
                      <div className="summary-text-fallback">
                        {selectedTranscription.summary}
                      </div>
                    ) : (
                      'Суммаризация отсутствует'
                    )}
                  </div>
                )}
              </div>
              
              <div className="detail-section">
                <label>Имя файла:</label>
                <div className="detail-value">{selectedTranscription.filename}</div>
              </div>
              <div className="detail-section">
                <label>Дата создания:</label>
                <div className="detail-value">{formatDate(selectedTranscription.created_at)}</div>
              </div>
              {selectedTranscription.language_code && (
                <div className="detail-section">
                  <label>Определенный язык:</label>
                  <div className="detail-value">
                    <span className="language-badge">{selectedTranscription.language_code}</span>
                  </div>
                </div>
              )}
              {selectedTranscription.s3_url && (
                <div className="detail-section">
                  <label>S3 URL:</label>
                  <div className="detail-value">
                    <a
                      href={selectedTranscription.s3_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="s3-link"
                    >
                      {selectedTranscription.s3_url}
                    </a>
                  </div>
                </div>
              )}
              {selectedTranscription.transcription_data?.speaker_count && 
               selectedTranscription.transcription_data.speaker_count > 0 && (
                <div className="detail-section">
                  <label>👥 Количество спикеров:</label>
                  <div className="detail-value">
                    <strong>{selectedTranscription.transcription_data.speaker_count}</strong> участник(ов) диалога
                  </div>
                </div>
              )}
              {/* Полный текст транскрипции - отображается первым */}
              <div className="detail-section">
                <div className="detail-header">
                  <label>📝 Полный текст расшифровки:</label>
                  <div className="detail-header-buttons">
                    {!isEditing ? (
                      <>
                        <button
                          className="btn btn-small btn-edit"
                          onClick={handleEdit}
                        >
                          ✏️ Редактировать
                        </button>
                        <button
                          className="btn btn-small"
                          onClick={() => copyToClipboard(selectedTranscription.transcription_text)}
                        >
                          📋 Копировать
                        </button>
                        <button
                          className="btn btn-small btn-delete"
                          onClick={() => handleDelete(selectedTranscription)}
                          title="Удалить расшифровку"
                        >
                          🗑️ Удалить
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="btn btn-small btn-save"
                          onClick={handleSaveEdit}
                          disabled={saving}
                        >
                          {saving ? '💾 Сохранение...' : '💾 Сохранить'}
                        </button>
                        <button
                          className="btn btn-small btn-cancel"
                          onClick={handleCancelEdit}
                          disabled={saving}
                        >
                          ❌ Отмена
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {isEditing ? (
                  <textarea
                    className="transcription-edit-textarea"
                    value={editedText}
                    onChange={(e) => setEditedText(e.target.value)}
                    rows={15}
                    disabled={saving}
                  />
                ) : (
                  <div className="transcription-full-text">
                    {selectedTranscription.transcription_text || 'Текст расшифровки отсутствует'}
                  </div>
                )}
              </div>
              
              {/* Отображение расшифровки с указанием спикеров и временных меток */}
              {selectedTranscription.transcription_data?.chunks && 
               selectedTranscription.transcription_data.chunks.length > 0 && (
                <div className="detail-section">
                  <div className="detail-header">
                    <label>🎤 Расшифровка с указанием спикеров:</label>
                    <button
                      className="btn btn-small"
                      onClick={() => {
                        const chunksText = selectedTranscription.transcription_data.chunks
                          .map(chunk => {
                            const timeStr = chunk.startTimeMs !== undefined 
                              ? formatTime(chunk.startTimeMs) 
                              : chunk.startTime || '';
                            const speakerStr = chunk.speakerTag !== undefined 
                              ? `Спикер ${chunk.speakerTag}` 
                              : '';
                            return `[${timeStr}]${speakerStr ? ` ${speakerStr}:` : ''} ${chunk.text}`;
                          })
                          .join('\n');
                        copyToClipboard(chunksText);
                      }}
                    >
                      📋 Копировать расшифровку
                    </button>
                  </div>
                  <div className="transcription-chunks-container">
                    {selectedTranscription.transcription_data.chunks.map((chunk, idx) => {
                      const timeStr = chunk.startTimeMs !== undefined 
                        ? formatTime(chunk.startTimeMs) 
                        : chunk.startTime || '';
                      const endTimeStr = chunk.endTimeMs !== undefined 
                        ? formatTime(chunk.endTimeMs) 
                        : chunk.endTime || '';
                      const hasSpeaker = chunk.speakerTag !== undefined && chunk.speakerTag !== null;
                      const hasChannel = chunk.channelTag !== undefined && chunk.channelTag !== null;
                      
                      return (
                        <div key={idx} className="transcription-chunk-detailed">
                          <div className="chunk-header">
                            <div className="chunk-header-left">
                              {timeStr && (
                                <span className="chunk-time">
                                  {timeStr}{endTimeStr && ` - ${endTimeStr}`}
                                </span>
                              )}
                              {hasChannel && (
                                <span className="chunk-channel-badge" title="Канал аудио">
                                  📻 Канал {chunk.channelTag}
                                </span>
                              )}
                            </div>
                            <div className="chunk-header-right">
                              {hasSpeaker && (
                                <span className="chunk-speaker-badge">
                                  👤 Спикер {chunk.speakerTag}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="chunk-text-detailed">{chunk.text}</div>
                          {chunk.words && chunk.words.length > 0 && (
                            <div className="chunk-words">
                              {chunk.words.map((word, wordIdx) => {
                                const wordTimeStr = word.startTimeMs !== undefined 
                                  ? formatTime(word.startTimeMs) 
                                  : word.startTime || '';
                                return (
                                  <span key={wordIdx} className="word-item" title={wordTimeStr}>
                                    {word.word || word.text || ''}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Отображение списка спикеров и статистики, если есть */}
              {selectedTranscription.transcription_data?.speakers && 
               Array.isArray(selectedTranscription.transcription_data.speakers) &&
               selectedTranscription.transcription_data.speakers.length > 0 && (
                <div className="detail-section">
                  <label>👥 Участники диалога:</label>
                  <div className="speakers-list">
                    {selectedTranscription.transcription_data.speakers.map((speaker, idx) => (
                      <span key={idx} className="speaker-badge-inline">
                        Спикер {speaker}
                      </span>
                    ))}
                  </div>
                  
                  {/* Статистика по спикерам */}
                  {selectedTranscription.transcription_data?.speakerStats && 
                   Object.keys(selectedTranscription.transcription_data.speakerStats).length > 0 && (
                    <div className="speakers-stats-detailed">
                      {Object.entries(selectedTranscription.transcription_data.speakerStats).map(([speakerTag, stats]) => (
                        <div key={speakerTag} className="speaker-stat-card">
                          <div className="speaker-stat-header">
                            <span className="speaker-stat-label">👤 Спикер {speakerTag}</span>
                          </div>
                          <div className="speaker-stat-details">
                            {stats.wordsCount && (
                              <div className="stat-item">
                                <span className="stat-label">Слов:</span>
                                <span className="stat-value">{stats.wordsCount}</span>
                              </div>
                            )}
                            {stats.utteranceCount && (
                              <div className="stat-item">
                                <span className="stat-label">Высказываний:</span>
                                <span className="stat-value">{stats.utteranceCount}</span>
                              </div>
                            )}
                            {stats.totalSpeechMs && (
                              <div className="stat-item">
                                <span className="stat-label">Время речи:</span>
                                <span className="stat-value">{formatTime(stats.totalSpeechMs)}</span>
                              </div>
                            )}
                            {stats.speechRatio !== undefined && (
                              <div className="stat-item">
                                <span className="stat-label">Доля речи:</span>
                                <span className="stat-value">{(stats.speechRatio * 100).toFixed(1)}%</span>
                              </div>
                            )}
                            {stats.wordsPerSecond?.mean && (
                              <div className="stat-item">
                                <span className="stat-label">Слов/сек:</span>
                                <span className="stat-value">{stats.wordsPerSecond.mean.toFixed(2)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Анализ разговора */}
              {selectedTranscription.transcription_data?.conversationAnalysis && (
                <div className="detail-section">
                  <label>💬 Анализ разговора:</label>
                  <div className="conversation-analysis">
                    {selectedTranscription.transcription_data.conversationAnalysis.totalSpeechDurationMs && (
                      <div className="analysis-item">
                        <span className="analysis-label">Общее время речи:</span>
                        <span className="analysis-value">
                          {formatTime(selectedTranscription.transcription_data.conversationAnalysis.totalSpeechDurationMs)}
                        </span>
                      </div>
                    )}
                    {selectedTranscription.transcription_data.conversationAnalysis.totalSpeechRatio !== undefined && (
                      <div className="analysis-item">
                        <span className="analysis-label">Доля речи в разговоре:</span>
                        <span className="analysis-value">
                          {(selectedTranscription.transcription_data.conversationAnalysis.totalSpeechRatio * 100).toFixed(1)}%
                        </span>
                      </div>
                    )}
                    {selectedTranscription.transcription_data.conversationAnalysis.totalSimultaneousSilenceDurationMs && (
                      <div className="analysis-item">
                        <span className="analysis-label">Одновременное молчание:</span>
                        <span className="analysis-value">
                          {formatTime(selectedTranscription.transcription_data.conversationAnalysis.totalSimultaneousSilenceDurationMs)}
                          {' '}
                          ({(selectedTranscription.transcription_data.conversationAnalysis.totalSimultaneousSilenceRatio * 100).toFixed(1)}%)
                        </span>
                      </div>
                    )}
                    {selectedTranscription.transcription_data.conversationAnalysis.totalSimultaneousSpeechDurationMs !== undefined && (
                      <div className="analysis-item">
                        <span className="analysis-value">
                          Одновременная речь: {formatTime(selectedTranscription.transcription_data.conversationAnalysis.totalSimultaneousSpeechDurationMs)}
                          {' '}
                          ({(selectedTranscription.transcription_data.conversationAnalysis.totalSimultaneousSpeechRatio * 100).toFixed(1)}%)
                        </span>
                      </div>
                    )}
                    {selectedTranscription.transcription_data.conversationAnalysis.speakerInterrupts && 
                     selectedTranscription.transcription_data.conversationAnalysis.speakerInterrupts.length > 0 && (
                      <div className="analysis-item">
                        <span className="analysis-label">Перебивания:</span>
                        <span className="analysis-value">
                          {selectedTranscription.transcription_data.conversationAnalysis.speakerInterrupts.length} случаев
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Старая структура для обратной совместимости */}
              {selectedTranscription.transcription_data?.speakers && 
               !Array.isArray(selectedTranscription.transcription_data.speakers) &&
               Object.keys(selectedTranscription.transcription_data.speakers).length > 0 && (
                <div className="detail-section">
                  <label>📊 Статистика по участникам:</label>
                  <div className="speakers-stats">
                    {Object.entries(selectedTranscription.transcription_data.speakers).map(([speaker, data]) => (
                      <div key={speaker} className="speaker-stat-item">
                        <div className="speaker-stat-header">
                          <span className="speaker-stat-label">{speaker}</span>
                          <span className="speaker-stat-count">
                            {data.replica_count || (Array.isArray(data) ? data.length : 0)} реплик
                          </span>
                        </div>
                        {data.replicas && (
                          <div className="speaker-replicas">
                            {data.replicas.map((replica, idx) => (
                              <div key={idx} className="speaker-replica">
                                {replica.start_time && (
                                  <span className="replica-time">{replica.start_time}</span>
                                )}
                                <span className="replica-text">{replica.text}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {Array.isArray(data) && (
                          <div className="speaker-texts">
                            {data.map((text, idx) => (
                              <div key={idx} className="speaker-text">{text}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Дополнительные данные для обратной совместимости */}
              {selectedTranscription.transcription_data?.dialogue && 
               selectedTranscription.transcription_data.dialogue.length > 0 && (
                <div className="detail-section">
                  <label>💬 Диалог по ролям:</label>
                  <div className="dialogue-container">
                    {selectedTranscription.transcription_data.dialogue.map((item, idx) => (
                      <div key={idx} className="dialogue-item">
                        <div className="dialogue-speaker">
                          <span className="speaker-badge">{item.speaker_label}</span>
                          {item.start_time && (
                            <span className="dialogue-time">{item.start_time}</span>
                          )}
                        </div>
                        <div className="dialogue-text">{item.text}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={handleCloseDetails}>
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast уведомления */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}

      {/* Модальное окно подтверждения */}
      {confirmationModal && (
        <ConfirmationModal
          isOpen={confirmationModal.isOpen}
          title={confirmationModal.title}
          message={confirmationModal.message}
          onConfirm={confirmationModal.onConfirm}
          onCancel={confirmationModal.onCancel}
          confirmText={confirmationModal.confirmText}
          cancelText={confirmationModal.cancelText}
          confirmButtonType={confirmationModal.confirmButtonType}
        />
      )}

      {/* Модальное окно создания Mock-собеседования */}
      {showMockInterviewModal && (
        <div className="modal-overlay" onClick={handleCloseMockInterviewModal}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Создать Mock-собеседование</h2>
              <button className="btn-icon btn-close" onClick={handleCloseMockInterviewModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="company_id">Компания *</label>
                <select
                  id="company_id"
                  value={mockInterviewForm.company_id}
                  onChange={(e) => setMockInterviewForm({ ...mockInterviewForm, company_id: e.target.value })}
                  required
                  disabled={saving}
                >
                  <option value="">Выберите компанию</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="position">Название должности *</label>
                <input
                  type="text"
                  id="position"
                  value={mockInterviewForm.position}
                  onChange={(e) => setMockInterviewForm({ ...mockInterviewForm, position: e.target.value })}
                  required
                  maxLength={255}
                  placeholder="Например: Backend Developer"
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="grade">Грейд *</label>
                <select
                  id="grade"
                  value={mockInterviewForm.grade}
                  onChange={(e) => setMockInterviewForm({ ...mockInterviewForm, grade: e.target.value })}
                  required
                  disabled={saving}
                >
                  <option value="Junior">Junior</option>
                  <option value="Middle">Middle</option>
                  <option value="Senior">Senior</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="salary_range">Зарплатная вилка *</label>
                <input
                  type="text"
                  id="salary_range"
                  value={mockInterviewForm.salary_range}
                  onChange={(e) => setMockInterviewForm({ ...mockInterviewForm, salary_range: e.target.value })}
                  required
                  maxLength={255}
                  placeholder="Например: 150000-250000 руб."
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <div className="detail-header">
                  <label>Вопросы и ответы:</label>
                  <div className="detail-header-actions">
                    <button
                      type="button"
                      className="btn btn-small btn-secondary"
                      onClick={handleGenerateMockQuestion}
                      disabled={saving || generatingQuestion || mockInterviewForm.questions.length === 0}
                      title={mockInterviewForm.questions.length === 0 ? 'Сначала добавьте хотя бы один вопрос' : 'Сгенерировать новый вопрос по теме существующих'}
                    >
                      {generatingQuestion ? '⏳ Генерация...' : '✨ Сгенерировать вопрос'}
                    </button>
                    <button
                      className="btn btn-small btn-primary"
                      onClick={handleAddMockQuestion}
                      disabled={saving}
                    >
                      ➕ Добавить вопрос
                    </button>
                  </div>
                </div>
                {mockInterviewForm.questions.length === 0 ? (
                  <div className="summary-empty-state">
                    <p>Вопросы отсутствуют. Добавьте первый вопрос:</p>
                    <button
                      className="btn btn-primary"
                      onClick={handleAddMockQuestion}
                      disabled={saving}
                    >
                      ➕ Добавить вопрос
                    </button>
                  </div>
                ) : (
                  <div className="questions-list-container">
                    {mockInterviewForm.questions.map((item, index) => (
                      <div key={index} className="question-item-container">
                        <div className="question-item-header">
                          <span className="question-number">{index + 1}.</span>
                          <div className="question-item-actions">
                            <button
                              className="btn btn-tiny btn-delete"
                              onClick={() => handleDeleteMockQuestion(index)}
                              disabled={saving}
                            >
                              🗑️ Удалить
                            </button>
                          </div>
                        </div>
                        <div className="question-edit-form">
                          <div className="question-edit-field">
                            <label>Вопрос:</label>
                            <textarea
                              className="question-edit-textarea"
                              value={item.question}
                              onChange={(e) => handleUpdateMockQuestion(index, 'question', e.target.value)}
                              rows={2}
                              disabled={saving}
                              placeholder="Введите вопрос..."
                            />
                          </div>
                          <div className="question-edit-field">
                            <div className="form-group-label-row">
                              <label>Ответ:</label>
                              <button
                                type="button"
                                className="btn btn-small btn-secondary btn-regenerate-answer"
                                onClick={() => handleRegenerateMockAnswer(index)}
                                disabled={saving || regeneratingAnswerIndex !== null || !(item.question || '').trim()}
                                title={!(item.question || '').trim() ? 'Сначала введите текст вопроса' : 'Пересоздать эталонный ответ нейросетью'}
                              >
                                {regeneratingAnswerIndex === index ? '⏳ Пересоздание...' : '🔄 Пересоздать ответ'}
                              </button>
                            </div>
                            <textarea
                              className="question-edit-textarea"
                              value={item.answer}
                              onChange={(e) => handleUpdateMockQuestion(index, 'answer', e.target.value)}
                              rows={3}
                              disabled={saving || regeneratingAnswerIndex === index}
                              placeholder="Введите ответ..."
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-primary"
                onClick={handleSubmitMockInterview}
                disabled={saving}
              >
                {saving ? '💾 Создание...' : '💾 Создать'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleCloseMockInterviewModal}
                disabled={saving}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TranscriptionsTab;

